import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense } from "react"
import { ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles"
import {
  Container,
  Popover,
  Typography,
  Box,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Avatar,
  AvatarGroup,
  IconButton,
  ListItemIcon,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText
} from "@mui/material"
import {
  GridView,
  Timeline,
  Share,
  AccessTime,
  Image as ImageIcon,
  Delete,
  Add,
  PersonRemove,
  NotificationsActive,
  CameraAlt,
  ArrowUpward,
} from "@mui/icons-material"
import { motion, AnimatePresence } from "framer-motion"
import { useAuth } from "../context/AuthProvider.tsx"
import { fetchCapsules } from "../api/GetCapsules.ts"
import DeleteCapsule from "../api/DeleteCapsule.ts"
import AddAccess from "../api/AddAccess.ts"
import RemoveAccess from "../api/RemoveAccess.ts"
import { useNavigate, useLocation } from "react-router-dom"
import { fetchAuthSession } from "@aws-amplify/auth"
import { type ThemeType, themeConfigs } from "../components/ThemeConfigs.ts"
import { useSwipeable } from "react-swipeable"
import { lazy as lazyImport } from "react"
import { AddAccessDialog, DeleteCapsuleDialog, RemoveAccessDialog, JoinCapsuleDialog } from "../components/Dialogs.tsx"
import LoadingCircle from "../components/LoadingCircle.tsx"

// Lazy loaded components for better performance
const WizardModal = lazyImport(() => import("../components/WizardModal.tsx"))
const UpgradeModal = lazyImport(() => import("../components/UpgradeModal.tsx"))

interface Capsule {
  capsuleID: number
  title: string
  description: string
  images: { image: string }[]
  unlock: string
  owner: string
  access: string[]
  theme: string
  print: boolean
  imageUrl?: string
  code?: string
  created?: string
}

interface CountdownProps {
  targetDate: Date
  isUnlocked: boolean
  isTimelineView?: boolean
  creationDate?: Date // Use Date type for creation date
}

// Update the theme creation to be mutable
let baseTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#7B2CBF",
      light: "#9D4EDD",
      dark: "#5A189A",
    },
    secondary: {
      main: "#E31B88",
      light: "#FF3399",
      dark: "#C41E3A",
    },
    background: {
      default: "#0A0F15",
      paper: "#121A21",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
        },
      },
    },
  },
})

baseTheme = responsiveFontSizes(baseTheme)

const CountdownTimer: React.FC<
  CountdownProps & { currentTheme: ThemeType; progress?: number; createdTime?: string }
> = ({ targetDate, isUnlocked, currentTheme, progress: externalProgress, createdTime }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })
  const [pulseAnimation, setPulseAnimation] = useState(false)
  const [calculatedProgress, setCalculatedProgress] = useState(0)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const themeConfig = themeConfigs[currentTheme]

  // Use either external progress or calculated progress
  const progress = typeof externalProgress !== "undefined" ? externalProgress : calculatedProgress

  // Animation for the progress ring
  const progressVariants = {
    initial: { strokeDashoffset: 283 }, // 2π × 45 (circle radius)
    animate: {
      strokeDashoffset: 283 - (283 * progress) / 100,
      transition: { duration: 1.5, ease: "easeInOut" },
    },
  }

  // Using stable refs to track values between renders
  const lastSecondsRef = useRef(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeLeftRef = useRef(timeLeft)
  const progressRef = useRef(calculatedProgress)
  const pulseTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Memoize the calculation function to prevent unnecessary recreations
  const calculateTimeLeftFunc = useCallback(() => {
    // If unlocked, maintain zeros
    if (isUnlocked) {
      // Only update state if values need to change
      if (
        timeLeftRef.current.days !== 0 ||
        timeLeftRef.current.hours !== 0 ||
        timeLeftRef.current.minutes !== 0 ||
        timeLeftRef.current.seconds !== 0
      ) {
        const zeroState = { days: 0, hours: 0, minutes: 0, seconds: 0 }
        timeLeftRef.current = zeroState
        setTimeLeft(zeroState)
      }

      // Only update progress if needed
      if (progressRef.current !== 100) {
        progressRef.current = 100
        setCalculatedProgress(100)
      }
      return
    }

    // Regular countdown logic
    const now = new Date().getTime()
    const unlockTime = targetDate.getTime()
    const difference = unlockTime - now

    if (difference > 0) {
      // Calculate time components
      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
      const minutes = Math.floor((difference / 1000 / 60) % 60)
      const seconds = Math.floor((difference / 1000) % 60)

      // Check for seconds change for animation
      if (seconds !== lastSecondsRef.current && !isUnlocked) {
        // Only trigger animation if not already pulsing
        setPulseAnimation(true)

        // Clear any existing timeout to prevent multiple timeouts
        if (pulseTimeoutRef.current) {
          clearTimeout(pulseTimeoutRef.current)
        }

        // Set new timeout
        pulseTimeoutRef.current = setTimeout(() => {
          setPulseAnimation(false)
          pulseTimeoutRef.current = null
        }, 500)
      }

      // Update seconds reference
      lastSecondsRef.current = seconds

      // Compare to ref value to minimize updates
      const newTimeLeft = { days, hours, minutes, seconds }
      if (
        timeLeftRef.current.days !== days ||
        timeLeftRef.current.hours !== hours ||
        timeLeftRef.current.minutes !== minutes ||
        timeLeftRef.current.seconds !== seconds
      ) {
        timeLeftRef.current = newTimeLeft
        setTimeLeft(newTimeLeft)
      }

      // Calculate progress based on hours instead of days
      // Use the provided creation time or estimate it if not available
      let creationTime: number

      if (createdTime) {
        // Use the actual creation date if provided
        creationTime = new Date(createdTime).getTime()
      } else {
        // Fallback to an estimation - assume the capsule is halfway through its lifetime
        creationTime = now - (unlockTime - now)
      }

      // Calculate total duration and elapsed time in hours for better precision
      const totalDurationMs = unlockTime - creationTime
      const elapsedTimeMs = now - creationTime

      // Calculate progress as percentage of elapsed time (based on hours)
      const progressPercentage = Math.min(100, Math.max(0, (elapsedTimeMs / totalDurationMs) * 100))

      // Only update if significant change (performance optimization)
      if (Math.abs(progressRef.current - progressPercentage) > 0.5) {
        progressRef.current = progressPercentage
        setCalculatedProgress(progressPercentage)
      }
    } else {
      // Time has expired
      if (
        timeLeftRef.current.days !== 0 ||
        timeLeftRef.current.hours !== 0 ||
        timeLeftRef.current.minutes !== 0 ||
        timeLeftRef.current.seconds !== 0
      ) {
        const zeroState = { days: 0, hours: 0, minutes: 0, seconds: 0 }
        timeLeftRef.current = zeroState
        setTimeLeft(zeroState)
      }

      if (progressRef.current !== 100) {
        progressRef.current = 100
        setCalculatedProgress(100)
      }
    }
  }, [isUnlocked, targetDate, createdTime])

  // Effect for one-time setup
  useEffect(() => {
    // Initial calculation
    calculateTimeLeftFunc()

    // Set up interval with memoized function reference
    // Update less frequently to improve performance
    intervalRef.current = setInterval(calculateTimeLeftFunc, 1000)

    // Clean up
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current)
        pulseTimeoutRef.current = null
      }
    }
  }, [calculateTimeLeftFunc]) // Dependency on the memoized function

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: isMobile ? "280px" : "320px",
        gap: 2,
        mt: isMobile ? 2 : 2,
      }}
    >
      {/* Progress Circle */}
      <Box
        sx={{
          position: "relative",
          width: isMobile ? 160 : 130,
          height: isMobile ? 160 : 130,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 1,
        }}
      >
        {/* Background Circle */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${themeConfig.accentColor}20 0%, transparent 70%)`,
            boxShadow: `0 0 20px ${themeConfig.accentColor}40`,
          }}
        />

        {/* SVG Progress Ring */}
        <Box sx={{ position: "absolute", width: "100%", height: "100%" }}>
          <motion.svg width="100%" height="100%" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle cx="50" cy="50" r="45" fill="none" stroke={`${themeConfig.accentColor}30`} strokeWidth="4" />

            {/* Progress circle */}
            <motion.circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={themeConfig.accentColor}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="283"
              initial="initial"
              animate="animate"
              variants={progressVariants}
              style={{
                transformOrigin: "center",
                rotate: "-90deg",
                filter: `drop-shadow(0 0 3px ${themeConfig.accentColor})`,
              }}
            />
          </motion.svg>
        </Box>

        {/* Content inside circle */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            p: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "white",
              opacity: 0.8,
              mb: 0.5,
              fontSize: isMobile ? "0.7rem" : "0.8rem",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Unlocks in
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "baseline",
              justifyContent: "center",
            }}
          >
            {timeLeft.days > 0 && (
              <>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      lineHeight: 1,
                      fontSize: isMobile ? "1.5rem" : "1.8rem",
                    }}
                  >
                    {timeLeft.days}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "white",
                      opacity: 0.8,
                      fontSize: "0.6rem",
                    }}
                  >
                    {timeLeft.days === 1 ? "DAY" : "DAYS"}
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    lineHeight: 1,
                    fontSize: isMobile ? "1.5rem" : "1.8rem",
                  }}
                >
                  :
                </Typography>
              </>
            )}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: "white",
                  lineHeight: 1,
                  fontSize: isMobile ? "1.5rem" : "1.8rem",
                }}
              >
                {timeLeft.hours.toString().padStart(2, "0")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  opacity: 0.8,
                  fontSize: "0.6rem",
                }}
              >
                HRS
              </Typography>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "white",
                lineHeight: 1,
                fontSize: isMobile ? "1.5rem" : "1.8rem",
              }}
            >
              :
            </Typography>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: "white",
                  lineHeight: 1,
                  fontSize: isMobile ? "1.5rem" : "1.8rem",
                }}
              >
                {timeLeft.minutes.toString().padStart(2, "0")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  opacity: 0.8,
                  fontSize: "0.6rem",
                }}
              >
                MIN
              </Typography>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "white",
                lineHeight: 1,
                fontSize: isMobile ? "1.5rem" : "1.8rem",
              }}
            >
              :
            </Typography>

            <Box sx={{ textAlign: "center" }}>
              <motion.div animate={pulseAnimation ? { scale: 1.1 } : { scale: 1 }} transition={{ duration: 0.3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    lineHeight: 1,
                    fontSize: isMobile ? "1.5rem" : "1.8rem",
                  }}
                >
                  {timeLeft.seconds.toString().padStart(2, "0")}
                </Typography>
              </motion.div>
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  opacity: 0.8,
                  fontSize: "0.6rem",
                }}
              >
                SEC
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Progress Text */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          mt: -1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "white",
            opacity: 0.9,
            textAlign: "center",
            fontSize: isMobile ? "0.75rem" : "0.8rem",
          }}
        >
          {isUnlocked
            ? "Ready to unlock!"
            : progress < 5
              ? "Just started"
              : progress < 25
                ? "Early stages"
                : progress < 50
                  ? "Getting there"
                  : progress < 75
                    ? "More than halfway"
                    : "Almost there"}
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              color: themeConfig.accentColor,
              fontWeight: 600,
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Typography>
      </Box>
    </Box>
  )
}

const GetStartedScreen = ({ onCreateCapsule }: { onCreateCapsule: () => void }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  // Create animated particles for background effect
  const particles = useMemo(() => {
    return Array.from({ length: 15 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 5,
      duration: Math.random() * 20 + 15,
    }))
  }, [])

  // Animation variants for staggered children
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
  }

  const buttonVariants = {
    hover: {
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    tap: { scale: 0.98 }
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80vh",
        textAlign: isMobile ? "left" : "left",
        px: { xs: 2, sm: 6, md: 10 },
        pt: { xs: -4, sm: 6 },
        pb: { xs: 0, sm: 6 },
        position: "relative",
        overflow: "hidden",
        bgcolor: "transparent", // Ensure transparent background
        backdropFilter: "none",  // Prevent backdrop filters from affecting background
      }}
    >
      {/* Animated background particles */}
      {particles.map((particle, i) => (
        <Box
          key={i}
          component={motion.div}
          sx={{
            position: "absolute",
            width: particle.size,
            height: particle.size,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
            boxShadow: "0 0 8px rgba(123, 44, 191, 0.8)",
            opacity: 0.4,
            zIndex: 0,
          }}
          animate={{
            x: ["0%", "100%", "50%", "80%", "20%", "0%"],
            y: ["0%", "60%", "20%", "80%", "40%", "0%"],
            opacity: [0.2, 0.5, 0.3, 0.6, 0.4, 0.2],
          }}
          transition={{
            x: { duration: particle.duration, ease: "easeInOut", repeat: Infinity },
            y: { duration: particle.duration * 0.8, ease: "easeInOut", repeat: Infinity },
            opacity: { duration: particle.duration * 0.5, ease: "easeInOut", repeat: Infinity },
            delay: particle.delay,
          }}
          initial={{ x: `${particle.x}%`, y: `${particle.y}%` }}
        />
      ))}

      {/* Left side - Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          width: isMobile ? "100%" : "50%",
          zIndex: 2,
          position: "relative"
        }}
      >
        <Box
          sx={{
            maxWidth: isMobile ? "90%" : "90%",
            mb: isMobile ? 8 : 0
          }}
        >
          <motion.div variants={itemVariants}>
            <Typography
              variant={isMobile ? "h3" : "h3"}
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: "linear-gradient(135deg, #fff, #9D4EDD)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -5,
                  left: 0,
                  width: "40%",
                  height: "4px",
                  background: "linear-gradient(90deg, #7B2CBF, transparent)",
                  borderRadius: "2px",
                }
              }}
            >
              Begin Your Time Capsule Journey
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                mb: 2,
                fontWeight: 600,
                fontStyle: "italic"
              }}
            >
              Create, Share, and Rediscover Memories
            </Typography>
          </motion.div>



          <motion.div variants={itemVariants}>
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                onClick={onCreateCapsule}
                sx={{
                  background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                  color: "white",
                  py: { xs: 1.5, md: 2 },
                  px: { xs: 4, md: 6 },
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", md: "1.2rem" },
                  fontWeight: 600,
                  boxShadow: "0 4px 20px rgba(123, 44, 191, 0.4)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                    transition: "all 0.5s ease",
                  },
                  "&:hover::before": {
                    left: "100%",
                  },
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(227, 27, 136, 0.6)",
                  }
                }}
              >
                Create Your First Capsule
              </Button>
            </motion.div>
          </motion.div>
        </Box>
      </motion.div>

      {/* Right side - Illustration */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          width: isMobile ? "100%" : "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: isMobile ? "80%" : "90%",
            maxWidth: "400px",
            aspectRatio: "1/1",
            mt: isMobile ? 0 : 0
          }}
        >
          {/* Illustration with animations */}
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Background glow effect */}
            <Box
              sx={{
                position: "absolute",
                width: "80%",
                height: "80%",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(123, 44, 191, 0.3) 0%, rgba(123, 44, 191, 0) 70%)",
                filter: "blur(15px)",
                animation: "pulse 3s infinite alternate",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)", opacity: 0.5 },
                  "100%": { transform: "scale(1.2)", opacity: 0.8 }
                }
              }}
            />

            {/* Main capsule circle */}
            <motion.div
              initial={{ rotate: 0 }}
              animate={{
                rotate: 360,
                transition: {
                  duration: 120,
                  ease: "linear",
                  repeat: Infinity
                }
              }}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: "85%",
                  height: "85%",
                  borderRadius: "50%",
                  border: "2px dashed rgba(255,255,255,0.3)",
                }}
              />
            </motion.div>

            {/* Orbit elements */}
            {[0, 1, 2].map((_, i) => (
              <motion.div
                key={i}
                initial={{ rotate: i * 120 }}
                animate={{
                  rotate: i * 120 + 360,
                  transition: {
                    duration: 20 + i * 5,
                    ease: "linear",
                    repeat: Infinity
                  }
                }}
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "0%",
                    left: "50%",
                    transform: "translate(-50%, 0%)",
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: i === 0 ? "12px" : "50%",
                        background: i === 0
                          ? "linear-gradient(135deg, #7B2CBF, #5A189A)"
                          : i === 1
                            ? "linear-gradient(135deg, #E31B88, #C41E3A)"
                            : "linear-gradient(135deg, #3a86ff, #4361ee)",
                        boxShadow: `0 0 20px ${i === 0 ? "#7B2CBF80" : i === 1 ? "#E31B8880" : "#3a86ff80"}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        border: "2px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      {i === 0 ? <ImageIcon fontSize="small" /> : i === 1 ? <AccessTime fontSize="small" /> : <Share fontSize="small" />}
                    </Box>
                  </motion.div>
                </Box>
              </motion.div>
            ))}

            {/* Central capsule container */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <Box
                sx={{
                  width: "50%",
                  aspectRatio: "1/1",
                  borderRadius: "24px",
                  background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                  boxShadow: "0 0 30px rgba(123, 44, 191, 0.6), 0 0 10px rgba(227, 27, 136, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, transparent 70%)",
                    zIndex: 0
                  }
                }}
              >

              </Box>
            </motion.div>
          </Box>
        </Box>
      </motion.div>
    </Box>
  )
}

const AccessListPopover: React.FC<{
  anchorEl: HTMLElement | null
  onClose: () => void
  accessList: string[]
  owner: string
}> = ({ anchorEl, onClose, accessList, owner }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const navigate = useNavigate()

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  }

  // Use consistent accent color gradient
  const accentGradient = "linear-gradient(90deg, #7B2CBF, #E31B88)"

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        "& .MuiPaper-root": {
          background: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          backdropFilter: "blur(10px)",
          border: "1px solid",
          borderColor: "rgba(255, 255, 255, 0.1)",
          overflow: "hidden",
        },
      }}
    >
      <motion.div initial="hidden" animate="visible" variants={containerVariants}>
        <Box
          sx={{
            p: 3,
            width: isMobile ? 280 : 320,
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <motion.div variants={itemVariants}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              {accessList.length} {accessList.length === 1 ? "person" : "people"}{" "}
              {accessList.length === 1 ? "has" : "have"} access to this capsule
            </Typography>
          </motion.div>

          <List sx={{ p: 0 }}>
            {accessList.map((username) => (
              <motion.div key={username} variants={itemVariants} onClick={() => navigate(`/profile/${username}`)}>
                <ListItem
                  sx={{
                    px: 0,
                    py: 1,
                    borderRadius: 2,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${username}/profilepic.jpeg`}
                      sx={{
                        width: 40,
                        height: 40,
                        border: username === owner ? "2px solid #7B2CBF" : "none",
                      }}
                    >
                      {username.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: username === owner ? 600 : 400,
                            color: username === owner ? "#7B2CBF" : "text.primary",
                          }}
                        >
                          @{username}
                        </Typography>
                        {username === owner && (
                          <Chip
                            label="Owner"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              background: accentGradient,
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </motion.div>
            ))}
          </List>
        </Box>
      </motion.div>
    </Popover>
  )
}

// Enhanced CapsuleCard component with modern design
const CapsuleCard: React.FC<{
  capsule: Capsule
  isActive?: boolean
  onDelete?: () => void
  onAddAccess?: () => void
  onRemoveAccess?: () => void
  isUnlocked?: boolean
  isOwner?: boolean
  isGridView?: boolean
}> = React.memo(function CapsuleCard({
  capsule,
  isActive = false,
  onDelete,
  onRemoveAccess,
  isUnlocked = false,
  isOwner = false,
  onAddAccess,
  isGridView = false,
}) {
  const themeConfig = themeConfigs[(capsule.theme as ThemeType) || "classic"]
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const firstImage = capsule.imageUrl || "/polaroid.jpg"
  const [progress, setProgress] = useState(0)
  const unlockDate = new Date(capsule.unlock)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
  }

  // Use a ref to avoid unnecessary state updates
  const progressRef = useRef(progress)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const calculateProgress = () => {
      if (isUnlocked) {
        if (progressRef.current !== 100) {
          progressRef.current = 100
          setProgress(100)
        }
        return
      }

      const now = new Date().getTime()
      const unlock = unlockDate.getTime()
      const creationDate = capsule.created ? new Date(capsule.created).getTime() : now - (unlock - now) // Fallback if created is not available

      const total = unlock - creationDate
      const elapsed = now - creationDate
      const calculatedProgress = Math.min(100, Math.max(0, (elapsed / total) * 100))

      // Only update state if the progress changed by a meaningful amount
      if (Math.abs(progressRef.current - calculatedProgress) > 0.5) {
        progressRef.current = calculatedProgress
        setProgress(calculatedProgress)
      }
    }

    // Initial calculation
    calculateProgress()

    // Set up interval for ongoing calculations
    timerRef.current = setInterval(calculateProgress, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isUnlocked, unlockDate]) // Removed dependencies that can change frequently

  let fileType

  if (firstImage) {
    fileType = firstImage.includes("video") ? "video" : "image"
  }
  const navigate = useNavigate()

  return (
    <Card
      component={motion.div}
      sx={{
        width: isGridView ? "100%" : isMobile ? "70vw" : "500px",
        height: isGridView ? { xs: "420px", sm: "450px" } : { xs: "500px", sm: "550px" },
        position: "relative",
        borderRadius: "20px",
        ml: 0,
        overflow: "hidden",
        background: `linear-gradient(145deg, ${themeConfig.accentColor}08, #121A21)`,
        backdropFilter: "blur(10px)",
        border: "1px solid",
        borderColor: `${themeConfig.accentColor}20`,
        boxShadow: isActive
          ? `0 16px 40px rgba(0,0,0,0.3), 0 0 15px ${themeConfig.accentColor}30`
          : `0 8px 24px rgba(0,0,0,0.15), 0 0 5px ${themeConfig.accentColor}15`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          borderColor: `${themeConfig.accentColor}40`,
          boxShadow: isMobile
            ? `0 8px 32px ${themeConfig.accentColor}40`
            : `0 20px 60px rgba(0,0,0,0.3), 0 0 20px ${themeConfig.accentColor}30`,
        },
        m: isGridView ? 0 : "auto",
      }}
    >
      {/* Enhanced Image Section */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isGridView ? "50%" : "45%",
          overflow: "hidden",
          borderBottom: `1px solid ${themeConfig.accentColor}15`,
        }}
      >
        {/* Access List moved to image section */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))",
            "&:hover": {
              filter: "drop-shadow(0 6px 12px rgba(0, 0, 0, 0.5))",
            },
          }}
        >
          <AvatarGroup
            max={3}
            onClick={handleAvatarClick}
            sx={{
              cursor: "pointer",
              "& .MuiAvatar-root": {
                width: 28,
                height: 28,
                border: `1px solid ${themeConfig.accentColor}`,
                bgcolor: `${themeConfig.accentColor}40`,
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                "&:hover": {
                  transform: "scale(1.1)",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
                },
              },
            }}
          >
            {capsule.access.map((user, i) => (
              <Avatar
                key={i}
                sx={{ color: "rgba(255,255,255,0.7)" }}
                src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${user}/profilepic.jpeg`}
                title={`@${user}`}
              >
                {user.charAt(0).toUpperCase()}
              </Avatar>
            ))}
          </AvatarGroup>
          <AccessListPopover
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            accessList={capsule.access}
            owner={capsule.owner}
          />
        </Box>
        {isUnlocked && firstImage ? (
          <Box
            component="img"
            src={firstImage}
            alt={capsule.title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.3s ease",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background: `linear-gradient(45deg, ${themeConfig.accentColor}20, ${themeConfig.accentColor}40)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          ></Box>
        )}

        {/* Overlay for Locked State */}
        {!isUnlocked && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: isMobile ? 1 : 2,
            }}
          >
            <CountdownTimer
              targetDate={unlockDate}
              isUnlocked={isUnlocked}
              currentTheme={(capsule.theme as ThemeType) || "classic"}
              progress={progress}
              createdTime={capsule.created}
            />
          </Box>
        )}
      </Box>

      {/* Content Section */}
      <Box
        sx={{
          height: isGridView ? "50%" : "55%",
          display: "flex",
          flexDirection: "column",
          p: isGridView ? { xs: 2.5, sm: 3 } : { xs: 2.5, sm: 3 },
          background: `linear-gradient(to bottom, ${themeConfig.accentColor}05, transparent)`,
          position: "relative",
        }}
      >
        {/* Header with Title and Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Typography
            variant={isGridView ? "h6" : "h5"}
            sx={{
              color: "white",
              fontWeight: 700,
              fontSize: isGridView ? { xs: "1.1rem", sm: "1.4rem" } : { xs: "1.5rem", sm: "2rem" },
              lineHeight: 1.2,
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              letterSpacing: "-0.01em",
              mb: 0.5,
            }}
          >
            {capsule.title}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {isOwner ? (
              <IconButton
                onClick={onDelete}
                disabled={isMobile && !isActive}
                size="small"
                sx={{
                  color: "white",
                  bgcolor: `${themeConfig.accentColor}20`,
                  backdropFilter: "blur(4px)",
                  "&:hover": {
                    bgcolor: "error.main",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <Delete sx={{ fontSize: 20 }} />
              </IconButton>
            ) : (
              <IconButton
                onClick={onRemoveAccess}
                disabled={isMobile && !isActive}
                size="small"
                sx={{
                  color: "white",
                  bgcolor: `${themeConfig.accentColor}20`,
                  backdropFilter: "blur(4px)",
                  "&:hover": {
                    bgcolor: "error.main",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <PersonRemove sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Description with fixed height */}
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.85)",
            fontSize: isGridView ? "0.8rem" : { xs: "0.875rem", sm: "1rem" },
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: isGridView ? 2 : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: 2,
            mt: 0.5,
            borderLeft: `2px solid ${themeConfig.accentColor}30`,
            pl: 1.5,
            py: 0.5,
            borderRadius: "2px",
          }}
        >
          {capsule.description}
        </Typography>

        {/* Status Chips and Access Info */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2.5,
            mt: "auto",
            alignItems: "center",
          }}
        >
          <Chip
            icon={<ImageIcon sx={{ color: `${themeConfig.accentColor}` }} />}
            label={`${capsule.images.length} Photos`}
            size="small"
            sx={{
              bgcolor: `${themeConfig.accentColor}15`,
              color: "white",
              backdropFilter: "blur(4px)",
              borderRadius: "12px",
              border: `1px solid ${themeConfig.accentColor}30`,
              fontWeight: 500,
              fontSize: isMobile ? "0.8rem" : "0.7rem",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              "&:hover": {
                bgcolor: `${themeConfig.accentColor}25`,
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          />
          <Chip
            icon={<AccessTime sx={{ color: `${themeConfig.accentColor}` }} />}
            label={
              isUnlocked
                ? `Unlocked ${unlockDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}`
                : `Unlocks ${unlockDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}`
            }
            size="small"
            sx={{
              bgcolor: `${themeConfig.accentColor}15`,
              color: "white",
              backdropFilter: "blur(4px)",
              borderRadius: "12px",
              border: `1px solid ${themeConfig.accentColor}30`,
              fontWeight: 500,
              fontSize: isMobile ? "0.8rem" : "0.7rem",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              "&:hover": {
                bgcolor: `${themeConfig.accentColor}25`,
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          {isUnlocked ? (
            <Button
              fullWidth
              onClick={() => navigate(`/capsule/${capsule.capsuleID}`)}
              sx={{
                background: `linear-gradient(135deg, ${themeConfig.accentColor}80, ${themeConfig.accentColor}60)`,
                color: "white",
                py: isGridView ? 1 : { xs: 1.2, sm: 1.6 },
                fontSize: isGridView ? "0.8rem" : { xs: "0.9rem", sm: "1rem" },
                fontWeight: 600,
                backdropFilter: "blur(4px)",
                borderRadius: "12px",
                border: `1px solid ${themeConfig.accentColor}40`,
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: `linear-gradient(135deg, ${themeConfig.accentColor}90, ${themeConfig.accentColor}70)`,
                  boxShadow: `0 6px 15px rgba(0,0,0,0.25), 0 0 10px ${themeConfig.accentColor}30`,
                },
                "&:active": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 3px 8px rgba(0,0,0,0.2), 0 0 5px ${themeConfig.accentColor}20`,
                },
              }}
            >
              View Memory
            </Button>
          ) : (
            <>
              <Button
                fullWidth
                onClick={() => navigate(`/camera/${capsule.capsuleID}`)}
                startIcon={!isGridView && <CameraAlt />}
                sx={{
                  background: `linear-gradient(135deg, ${themeConfig.accentColor}70, ${themeConfig.accentColor}50)`,
                  color: "white",
                  py: isGridView ? 1 : { xs: 1.2, sm: 1.6 },
                  fontSize: isGridView ? "0.8rem" : { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 600,
                  minWidth: 0,
                  backdropFilter: "blur(4px)",
                  borderRadius: "12px",
                  border: `1px solid ${themeConfig.accentColor}40`,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${themeConfig.accentColor}80, ${themeConfig.accentColor}60)`,
                    boxShadow: `0 6px 15px rgba(0,0,0,0.25), 0 0 10px ${themeConfig.accentColor}30`,
                  },
                  "&:active": {
                    transform: "translateY(-1px)",
                    boxShadow: `0 3px 8px rgba(0,0,0,0.2), 0 0 5px ${themeConfig.accentColor}20`,
                  },
                }}
              >
                {isGridView ? <CameraAlt sx={{ fontSize: "1.1rem" }} /> : "Add"}
              </Button>
              <Button
                fullWidth
                onClick={onAddAccess}
                startIcon={!isGridView && <Share />}
                sx={{
                  background: `linear-gradient(135deg, ${themeConfig.accentColor}70, ${themeConfig.accentColor}50)`,
                  color: "white",
                  py: isGridView ? 1 : { xs: 1.2, sm: 1.6 },
                  fontSize: isGridView ? "0.8rem" : { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 600,
                  minWidth: 0,
                  backdropFilter: "blur(4px)",
                  borderRadius: "12px",
                  border: `1px solid ${themeConfig.accentColor}40`,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${themeConfig.accentColor}80, ${themeConfig.accentColor}60)`,
                    boxShadow: `0 6px 15px rgba(0,0,0,0.25), 0 0 10px ${themeConfig.accentColor}30`,
                  },
                  "&:active": {
                    transform: "translateY(-1px)",
                    boxShadow: `0 3px 8px rgba(0,0,0,0.2), 0 0 5px ${themeConfig.accentColor}20`,
                  },
                }}
              >
                {isGridView ? <Share sx={{ fontSize: "1.1rem" }} /> : "Share"}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Card>
  )
})

// Loading screen component
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "#0A0F15",
      zIndex: 9999,
    }}
  >
    <LoadingCircle />
  </Box>
)

// Helper function to determine if a timeline node should be visible (activated)
// This activates nodes when they are approximately at the center of the screen
const isNodeVisible = (
  scrollProgress: number,
  yearIndex: number,
  itemIndex: number,
  totalYears: number,
  capsulesByYear: Record<string, Capsule[]>,
) => {
  // Calculate the total number of items
  const totalItems = Object.values(capsulesByYear).reduce((sum, capsules) => sum + capsules.length, 0)

  // Calculate the approximate position of this node in the entire timeline
  const yearProgressStart = (yearIndex / totalYears) * 100

  // Calculate the year's portion of the scroll range
  const yearSize = 100 / totalYears

  // Calculate item's position within its year
  const itemsInYear = capsulesByYear[Object.keys(capsulesByYear)[yearIndex]].length
  const itemPosition = (itemIndex / itemsInYear) * yearSize

  // Calculate the approximate center point for this node
  const nodeCenterPoint = yearProgressStart + itemPosition

  // Set a smaller threshold around the center point for more precise activation
  // Using a smaller value (5%) makes the activation happen closer to the exact center
  const threshold = 3

  // Node is visible when the scroll position is within threshold of the node's center point
  return Math.abs(scrollProgress - nodeCenterPoint) < threshold
}

// Redesigned Timeline View with centered timeline
const TimelineView: React.FC<{ capsules: Capsule[] }> = ({ capsules }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const navigate = useNavigate()
  const { user } = useAuth()
  const [scrollProgress, setScrollProgress] = useState(0)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [timelineParticles, setTimelineParticles] = useState<
    Array<{ y: number; size: number; speed: number; opacity: number }>
  >([])

  // Generate timeline particles
  useEffect(() => {
    const particles = Array.from({ length: 15 }, () => ({
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      speed: Math.random() * 0.5 + 0.2,
      opacity: Math.random() * 0.5 + 0.3,
    }))
    setTimelineParticles(particles)
  }, [])

  // Scroll to top function
  const scrollToTop = () => {
    // Use window.scrollTo since we're using window scroll
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  // Sort capsules by unlock date
  const sortedCapsules = useMemo(() => {
    return [...capsules].sort((a, b) => {
      return new Date(a.unlock).getTime() - new Date(b.unlock).getTime()
    })
  }, [capsules])

  // Group capsules by year
  const capsulesByYear = useMemo(() => {
    const grouped: Record<string, Capsule[]> = {}

    sortedCapsules.forEach((capsule) => {
      const year = new Date(capsule.unlock).getFullYear().toString()
      if (!grouped[year]) {
        grouped[year] = []
      }
      grouped[year].push(capsule)
    })

    return grouped
  }, [sortedCapsules])

  // Get years in chronological order
  const years = useMemo(() => {
    return Object.keys(capsulesByYear).sort()
  }, [capsulesByYear])

  // Use window scroll instead of container scroll
  useEffect(() => {
    // Function to handle scroll events from any source
    const handleScroll = () => {
      try {
        // Calculate document height - use the most reliable method for mobile
        const docHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight,
          document.body.clientHeight,
          document.documentElement.clientHeight,
        )

        // Get viewport height
        const windowHeight = window.innerHeight

        // Get current scroll position - try multiple methods for better mobile compatibility
        const scrollY =
          window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0

        // Calculate scrollable distance
        const scrollableDistance = docHeight - windowHeight

        // Calculate scroll percentage with safeguards
        let newProgress = 0
        if (scrollableDistance > 10) {
          // Only calculate if we have meaningful scrollable content
          newProgress = Math.min(100, Math.max(0, (scrollY / scrollableDistance) * 100))
        } else if (scrollY > 0) {
          newProgress = 100 // If there's any scroll but not much content, show full progress
        }

        // Update state only if there's a meaningful change
        if (Math.abs(newProgress - scrollProgress) > 0.5) {
          setScrollProgress(newProgress)
          console.log("Mobile scroll progress:", newProgress, "scrollY:", scrollY, "docHeight:", docHeight)
        }

        // Show scroll-to-top button after scrolling down a bit
        setShowScrollTop(scrollY > 300)
      } catch (error) {
        console.error("Error in scroll handler:", error)
      }
    }

    // Add event listeners for both window and document to ensure we catch all scroll events
    window.addEventListener("scroll", handleScroll, { passive: true })
    document.addEventListener("scroll", handleScroll, { passive: true })

    // For mobile touch events that might not trigger scroll events properly
    document.addEventListener("touchmove", handleScroll, { passive: true })
    document.addEventListener("touchend", handleScroll, { passive: true })

    // Initial calculation
    setTimeout(handleScroll, 100) // Slight delay to ensure DOM is ready

    // Cleanup all listeners
    return () => {
      window.removeEventListener("scroll", handleScroll)
      document.removeEventListener("scroll", handleScroll)
      document.removeEventListener("touchmove", handleScroll)
      document.removeEventListener("touchend", handleScroll)
    }
  }, [scrollProgress]) // Add scrollProgress as dependency to prevent stale closures

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
      {/* Timeline Line */}
      <Box
        sx={{
          position: "absolute",
          top: "10px", // Position properly below header
          left: isMobile ? "5%" : "50%", // Left aligned on mobile, centered on desktop
          width: "4px", // Slightly thicker line for better visibility
          height: "99%", // Use viewport height with spacing
          zIndex: 0, // Lower z-index than nodes
          pointerEvents: "none",
          transform: isMobile ? "none" : "translateX(-50%)", // Only center on desktop
          willChange: "transform", // Hardware acceleration hint
        }}
      >
        {/* Background line */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%", // Full height
            bgcolor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "2px",
          }}
        />

        {/* Filled line that grows based on scroll progress */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: `${scrollProgress}%`,
            background: "linear-gradient(to bottom, #7B2CBF, #E31B88)",
            boxShadow: "0 0 8px rgba(123, 44, 191, 0.5)",
            transition: "height 0.2s ease-out", // Remove transition on mobile for better performance
            minHeight: "5px",
            borderRadius: "2px",
            willChange: "height", // Hint for browser optimization
            transform: "translateZ(0)", // Force hardware acceleration
          }}
        />
      </Box>

      {/* Scroll to top button */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: showScrollTop ? 1 : 0,
          scale: showScrollTop ? 1 : 0.8,
          pointerEvents: showScrollTop ? "auto" : "none",
        }}
        transition={{ duration: 0.2 }}
        style={{
          position: "fixed",
          bottom: isMobile ? 60 : 20,
          right: 20,
          zIndex: 100,
        }}
      >
        <IconButton
          onClick={scrollToTop}
          sx={{
            background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
            color: "white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #9D4EDD, #FF3399)",
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
            },
          }}
        >
          <ArrowUpward />
        </IconButton>
      </motion.div>

      <Box
        sx={{
          px: isMobile ? 2 : 4,
          py: 4,
          pb: 20,
          height: "auto",
          overflowY: "visible",
        }}
      >
        {years.map((year, yearIndex) => (
          <Box key={year} sx={{ mb: 6 }}>
            {/* Year Header - Now white text */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px 0px -100px 0px" }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.5rem", sm: "3.5rem" },
                  fontWeight: 800,
                  mb: 4,
                  textAlign: "left", // Always align left
                  paddingLeft: isMobile ? "15%" : "5%", // Add padding to move away from timeline
                  color: "white",
                  letterSpacing: "-0.02em",
                  position: "relative",
                  zIndex: 5, // Ensure it appears above the timeline
                }}
              >
                {year}
              </Typography>
            </motion.div>

            {/* Capsules for this year */}
            <Box sx={{ position: "relative" }}>
              {capsulesByYear[year].map((capsule, index) => {
                const unlockDate = new Date(capsule.unlock)
                const isUnlocked = new Date() > unlockDate
                const isOwner = capsule.owner === user?.username
                const themeConfig = themeConfigs[(capsule.theme as ThemeType) || "classic"]
                const isEven = index % 2 === 0

                // Format date nicely
                const formattedDate = unlockDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                })

                return (
                  <motion.div
                    key={capsule.capsuleID}
                    initial={{ opacity: 0.5, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px 0px -200px 0px" }}
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        mb: 8,
                        position: "relative",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "center" : "flex-start",
                        mt: 8,
                      }}
                    >
                      {/* Timeline node */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: isMobile ? "-1%" : "50%",
                          transform: isMobile ? "none" : "translateX(-50%)",
                          width: { xs: 20, sm: 60 },
                          height: { xs: 20, sm: 60 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 5,
                          top: isMobile ? -30 : -10,
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: 1000,
                          }}
                        >
                          {/* White circle dot */}
                          <Box
                            sx={{
                              width: 15,
                              height: 15,
                              borderRadius: "50%",
                              background: `white`,
                              position: "relative",
                              zIndex: 10, // Higher z-index to stay on top of the timeline
                              boxShadow:
                                scrollProgress >=
                                  (yearIndex * 100) / years.length +
                                  (index * 100) / (years.length * capsulesByYear[year].length)
                                  ? "0 0 5px #7B2CBF"
                                  : "none",
                              transitionProperty: "box-shadow",
                              transitionDuration: "0.3s",
                              transitionTimingFunction: "ease-in-out",
                              willChange: "box-shadow", // Hardware acceleration hint
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Left side - Image */}
                      <Box
                        sx={{
                          width: isMobile ? "85%" : "calc(50% - 60px)", // Adjusted width for mobile
                          mr: isMobile ? 0 : isEven ? 8 : 0, // Increased padding for better spacing
                          ml: isMobile ? "15%" : isEven ? 0 : 16, // Added left margin on mobile for timeline
                          order: isMobile ? 2 : isEven ? 0 : 2,
                          display: "flex",
                          justifyContent: isMobile ? "flex-start" : isEven ? "flex-end" : "flex-start",
                        }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.03, rotate: isEven ? -1 : 1 }}
                          transition={{ duration: 0.3 }}
                          style={{
                            width: isMobile ? "100%" : "85%",
                            maxWidth: isMobile ? "none" : "400px",
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              borderRadius: "16px",
                              overflow: "hidden",
                              boxShadow: `0 10px 30px rgba(0,0,0,0.3), 0 0 10px ${themeConfig.accentColor}40`,
                              transform: isEven ? "rotate(1deg)" : "rotate(-1deg)",
                              transition: "all 0.3s ease",
                              border: "4px solid",
                              borderColor: "rgba(255, 255, 255, 0.1)",
                              cursor: isUnlocked ? "pointer" : "default",
                            }}
                            onClick={() => isUnlocked && navigate(`/capsule/${capsule.capsuleID}`)}
                          >
                            <Box
                              component="img"
                              src={capsule.imageUrl || "/polaroid.jpg"}
                              alt={capsule.title}
                              sx={{
                                width: "100%",
                                aspectRatio: "4/3",
                                objectFit: "cover",
                                filter: isUnlocked ? "none" : "blur(10px) brightness(0.7)",
                                transition: "all 0.5s ease",
                              }}
                            />

                            {/* Lock overlay for locked capsules */}
                            {!isUnlocked && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "rgba(0,0,0,0.4)",
                                  backdropFilter: "blur(5px)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "white",
                                    fontWeight: 600,
                                    textAlign: "center",
                                    px: 2,
                                  }}
                                >
                                  Unlocks on{" "}
                                  {unlockDate.toLocaleDateString("en-US", {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </motion.div>
                      </Box>

                      {/* Right side - Abstract Content Display */}
                      <Box
                        sx={{
                          width: isMobile ? "85%" : "calc(50% - 60px)", // Match left side spacing
                          pl: isMobile ? 0 : isEven ? 4 : 8, // Increased padding for better spacing
                          pr: isMobile ? 0 : isEven ? 8 : 0, // Increased padding for better spacing
                          ml: isMobile ? "15%" : 0, // Added left margin on mobile for timeline
                          order: isMobile ? 3 : isEven ? 2 : 0,
                          mt: isMobile ? 3 : 0,
                          display: "flex",
                          justifyContent: isMobile ? "flex-start" : isEven ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            width: isMobile ? "100%" : "85%",
                            maxWidth: isMobile ? "none" : "400px",
                          }}
                        >
                          {/* Title with creative styling */}
                          <motion.div
                            whileHover={{
                              scale: 1.02,
                              x: isEven ? 5 : -5,
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            {/* Date label */}
                            <Typography
                              variant="caption"
                              sx={{
                                position: "absolute",
                                top: isMobile ? -30 : -30,
                                left: isMobile ? "15%" : "auto", // Align with timeline on mobile
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                fontWeight: 600,
                                color: "white",
                                textAlign: "left", // Left-align text
                                opacity: 0.9,
                              }}
                            >
                              {formattedDate}
                            </Typography>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 700,
                                color: "white",
                                mb: 2,
                                textShadow: `0 0 10px ${themeConfig.accentColor}80`,
                                position: "relative",
                                display: "inline-block",
                                "&:after": {
                                  content: '""',
                                  position: "absolute",
                                  bottom: -5,
                                  left: 0,
                                  width: "100%",
                                  height: "2px",
                                  background: `linear-gradient(to ${isEven ? "right" : "left"}, ${themeConfig.accentColor}, transparent)`,
                                },
                              }}
                            >
                              {capsule.title}
                            </Typography>
                          </motion.div>

                          {/* Description with abstract styling */}
                          <Box
                            sx={{
                              position: "relative",
                              mb: 3,
                              "&:before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: isMobile ? 0 : isEven ? 0 : "auto",
                                right: isEven ? "auto" : 0,
                                width: "3px",
                                height: "100%",
                                background: `linear-gradient(to bottom, ${themeConfig.accentColor}, transparent)`,
                                borderRadius: "3px",
                              },
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: "rgba(255,255,255,0.9)",
                                pl: isMobile ? 2 : isEven ? 2 : 0,
                                pr: isEven ? 0 : 2,
                                py: 1,
                                fontSize: { xs: "0.9rem", sm: "1rem" },
                                lineHeight: 1.6,
                                fontStyle: "italic",
                              }}
                            >
                              {capsule.description}
                            </Typography>
                          </Box>

                          {/* Status and metadata with creative styling */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {/* Image count with abstract styling */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "12px",
                                  background: `linear-gradient(135deg, ${themeConfig.accentColor}40, transparent)`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: `0 0 10px ${themeConfig.accentColor}30`,
                                }}
                              >
                                <ImageIcon sx={{ color: "white", fontSize: 20 }} />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "white",
                                  fontWeight: 500,
                                }}
                              >
                                {capsule.images.length} {capsule.images.length === 1 ? "Photo" : "Photos"} captured
                              </Typography>
                            </Box>

                            {/* Access list with abstract styling */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <AvatarGroup
                                max={3}
                                sx={{
                                  "& .MuiAvatar-root": {
                                    width: 32,
                                    height: 32,
                                    border: `2px solid ${themeConfig.accentColor}40`,
                                    boxShadow: `0 0 10px ${themeConfig.accentColor}30`,
                                  },
                                }}
                              >
                                {capsule.access.map((user, i) => (
                                  <Avatar
                                    key={i}
                                    sx={{ color: "rgba(255,255,255,0.7)" }}
                                    src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${user}/profilepic.jpeg`}
                                    title={`@${user}`}
                                  >
                                    {user.charAt(0).toUpperCase()}
                                  </Avatar>
                                ))}
                              </AvatarGroup>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "white",
                                  fontWeight: 500,
                                }}
                              >
                                {capsule.access.length} {capsule.access.length === 1 ? "person has" : "people have"}{" "}
                                access
                              </Typography>
                            </Box>

                            {/* Action button with creative styling */}
                            <Box sx={{ mt: 1 }}>
                              <Button
                                variant="contained"
                                disabled={!isUnlocked}
                                onClick={() => navigate(`/capsule/${capsule.capsuleID}`)}
                                sx={{
                                  background: isUnlocked ? themeConfig.accentColor : "rgba(255, 255, 255, 0.1)",
                                  color: "white",
                                  borderRadius: "12px",
                                  px: 3,
                                  py: 1,
                                  fontWeight: 600,
                                  boxShadow: isUnlocked ? `0 5px 15px ${themeConfig.accentColor}40` : "none",
                                  backdropFilter: "blur(5px)",
                                  border: "1px solid",
                                  borderColor: isUnlocked ? `${themeConfig.accentColor}60` : "rgba(255, 255, 255, 0.2)",
                                  "&:hover": {
                                    background: isUnlocked ? themeConfig.accentColor : "rgba(255, 255, 255, 0.1)",
                                    boxShadow: isUnlocked ? `0 8px 20px ${themeConfig.accentColor}60` : "none",
                                    transform: isUnlocked ? "translateY(-2px)" : "none",
                                  },
                                  "&:disabled": {
                                    color: "rgba(255, 255, 255, 0.4)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                              >
                                {isUnlocked ? "Open Memory" : "Locked"}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                )
              })}
            </Box>
          </Box>
        ))}

        {/* Empty state */}
        {years.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
              textAlign: "center",
              px: 3,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Typography variant="h4" sx={{ mb: 2, color: "primary.main" }}>
                No time capsules yet
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: "text.secondary", maxWidth: "600px" }}>
                Create your first time capsule to start preserving memories for the future.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{
                  background: "linear-gradient(45deg, #7B2CBF, #E31B88)",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(123, 44, 191, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(45deg, #9D4EDD, #FF3399)",
                    boxShadow: "0 6px 25px rgba(123, 44, 191, 0.4)",
                  },
                }}
              >
                Create a Capsule
              </Button>
            </motion.div>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default function HomePage() {
  const [view, setView] = useState<"grid" | "timeline">("grid")
  const [loading, setLoading] = useState(true)
  const [capsules, setCapsules] = useState<Capsule[]>([])
  const [currentCapsuleIndex, setCurrentCapsuleIndex] = useState(0)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [capsuleToDelete, setCapsuleToDelete] = useState<number | null>(null)
  const [removeAccessDialogOpen, setRemoveAccessDialogOpen] = useState(false)
  const { user } = useAuth()
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"))
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)
  const [friends, setFriends] = useState<{ username: string; name: string; picture: string }[]>([])
  const [selectedTheme, setSelectedTheme] = useState<ThemeType>("classic")
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [joinCapsuleId, setJoinCapsuleId] = useState<string | null>(null)

  const location = useLocation()
  const theme = useTheme() // Access the theme here

  useEffect(() => {
    // Check URL parameters first
    const params = new URLSearchParams(window.location.search)
    if (params.get("action") === "join") {
      const capsuleId = params.get("capsuleId")
      if (capsuleId) {
        setJoinCapsuleId(capsuleId)
        return
      }
    }

    // If no URL parameters, check sessionStorage for saved parameters
    const redirectUrl = sessionStorage.getItem("redirectAfterLogin")
    if (redirectUrl) {
      try {
        // Extract query parameters from the saved URL string
        // The format is expected to be like: /?action=join&capsuleId=1736302899964
        const savedParams = new URLSearchParams(redirectUrl.split("?")[1])

        if (savedParams.get("action") === "join") {
          const capsuleId = savedParams.get("capsuleId")
          if (capsuleId) {
            setJoinCapsuleId(capsuleId)
            // Clear the saved parameters to prevent showing the dialog again on refresh
            sessionStorage.removeItem("redirectAfterLogin")
          }
        }
      } catch (error) {
        console.error("Error parsing saved redirect URL:", error)
      }
    }
  }, [])

  // Handle refresh capsules from navigation state
  useEffect(() => {
    if (location.state?.refreshCapsules && user?.username) {
      setLoading(true)
      const loadCapsules = async () => {
        try {
          const fetchedCapsules = await fetchCapsules(user.username)
          const sortedCapsules = fetchedCapsules.sort(
            (a, b) => new Date(a.unlock).getTime() - new Date(b.unlock).getTime(),
          )
          setCapsules(sortedCapsules)

          if (sortedCapsules.length > 0) {
            setCurrentCapsuleIndex(sortedCapsules.length - 1) // Reset to the last capsule
            setSelectedTheme((sortedCapsules[sortedCapsules.length - 1].theme as ThemeType) || "classic")
          }
        } catch (error) {
          console.error("Error fetching capsules:", error)
        } finally {
          setLoading(false)
        }
      }
      loadCapsules()

      // Clear the state after processing
      window.history.replaceState({}, document.title)
    }
  }, [location.state, user])

  const currentCapsule = useMemo(() => capsules[currentCapsuleIndex], [capsules, currentCapsuleIndex])

  useEffect(() => {
    const loadCapsules = async () => {
      if (user?.username) {
        try {
          const fetchedCapsules = await fetchCapsules(user.username)
          const sortedCapsules = fetchedCapsules.sort(
            (a, b) => new Date(a.unlock).getTime() - new Date(b.unlock).getTime(),
          )
          setCapsules(sortedCapsules)

          const savedIndex = localStorage.getItem("currentCapsuleIndex")
          const parsedIndex = savedIndex ? Number.parseInt(savedIndex, 10) : 0

          if (sortedCapsules.length > 0) {
            const validIndex = Math.min(Math.max(parsedIndex, 0), sortedCapsules.length - 1)
            setCurrentCapsuleIndex(validIndex)
            setSelectedTheme((sortedCapsules[validIndex].theme as ThemeType) || "classic")
          } else {
            setCurrentCapsuleIndex(0)
            setSelectedTheme("classic")
          }
        } catch (error) {
          console.error("Error fetching capsules:", error)
        } finally {
          setLoading(false)
        }
      }
    }
    loadCapsules()
  }, [user])

  useEffect(() => {
    if (view === "timeline") {
      setSelectedTheme("classic")
    } else if (capsules.length > 0) {
      const currentCapsule = capsules[currentCapsuleIndex]
      if (currentCapsule && currentCapsule.theme) {
        setSelectedTheme(currentCapsule.theme as ThemeType)
      } else {
        setSelectedTheme("classic")
      }
      localStorage.setItem("currentCapsuleIndex", currentCapsuleIndex.toString())
    }
  }, [currentCapsuleIndex, capsules, view])

  const handleNextCapsule = () => {
    if (capsules.length > 0) {
      setCurrentCapsuleIndex((prevIndex) => (prevIndex < capsules.length - 1 ? prevIndex + 1 : 0))
    }
  }

  const handleOpenWizard = async () => {
    if (user?.subscriptionStatus !== "premium") {
      const capsules = await fetchCapsules(user!.username)
      const capsuleCount = capsules.length
      if (capsuleCount > 2) {
        setUpgradeModalOpen(true)
      } else {
        setIsWizardOpen(true)
      }
    } else {
      setIsWizardOpen(true)
    }
  }

  const handleCloseWizard = () => {
    setIsWizardOpen(false)
  }

  const handleSubmitCapsule = (formData: any) => {
    setIsWizardOpen(false)
  }

  const handleCloseJoinDialog = () => {
    window.history.replaceState({}, "", `${window.location.pathname}`)
    setJoinCapsuleId(null)
  }

  const handleAddAccess = async () => {
    if (currentCapsule && selectedUsers.length > 0) {
      setLoading(true)
      for (const newUser of selectedUsers) {
        await AddAccess(currentCapsule.capsuleID.toString(), newUser)
      }
      const updatedCapsules = await fetchCapsules(user?.username!)
      setCapsules(updatedCapsules)
      setLoading(false)
    }
    handleCloseDialog()
  }

  const handleCloseUpgradeModal = () => {
    setUpgradeModalOpen(false)
  }

  const handleRemoveAccess = async () => {
    if (currentCapsule) {
      handleNextCapsule()
      setRemoveAccessDialogOpen(false)
      await RemoveAccess(currentCapsule.capsuleID.toString(), user?.username!)
      const updatedCapsules = await fetchCapsules(user?.username!)
      setCapsules(updatedCapsules)
    }
  }

  const memoizedImageUrls = useMemo(() => {
    return capsules.map((capsule) => ({
      ...capsule,
      imageUrl: capsule.imageUrl || "/polaroid.jpg",
    }))
  }, [capsules])

  const totalCards = useMemo(() => {
    const placeholdersNeeded = Math.min(2, Math.max(0, 3 - capsules.length))
    return capsules.length + placeholdersNeeded
  }, [capsules.length])

  const handleNext = useCallback(() => {
    setCurrentCapsuleIndex((prev) => (prev + 1) % totalCards)
  }, [totalCards])

  const handlePrev = useCallback(() => {
    setCurrentCapsuleIndex((prev) => (prev - 1 + totalCards) % totalCards)
  }, [totalCards])

  const handlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    trackMouse: true,
  })

  const PlaceholderCapsule = React.memo(function PlaceholderCapsule({ isActive }: { isActive: boolean }) {
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const classicTheme = themeConfigs["classic"]

    // Enhanced animation variants
    const cardVariants = {
      hover: {
        boxShadow: isMobile ? `0 12px 36px ${classicTheme.accentColor}30` : "0 16px 40px rgba(123, 44, 191, 0.25)",
      },
    }

    const itemVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5,
          ease: "easeOut",
        },
      },
    }

    // Create a gradient based on theme colors
    const gradientBg = `linear-gradient(135deg, #121A21 0%, ${classicTheme.accentColor}10 100%)`
    const buttonGradient = `linear-gradient(to right, ${classicTheme.accentColor}, #E31B88)`

    return (
      <Card
        component={motion.div}
        variants={cardVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
        whileTap="tap"
        sx={{
          width: { xs: "70vw", sm: "100%" },
          height: { xs: "500px", sm: "450px" },
          position: "relative",
          borderRadius: "24px",
          overflow: "hidden",
          background: gradientBg,
          backdropFilter: "blur(10px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          border: "1px solid",
          borderColor: "rgba(255, 255, 255, 0.1)",
          boxShadow: isActive ? `0 12px 36px ${classicTheme.accentColor}30` : `0 8px 24px rgba(0, 0, 0, 0.2)`,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Abstract decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${classicTheme.accentColor}20 0%, transparent 70%)`,
            opacity: 0.6,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: -30,
            left: -30,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: `radial-gradient(circle, #E31B8830 0%, transparent 70%)`,
            opacity: 0.5,
          }}
        />

        <CardContent
          component={motion.div}
          initial="hidden"
          animate="visible"
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: { xs: 4, sm: 3 },
            position: "relative",
            zIndex: 2,
          }}
        >
          <motion.div variants={itemVariants}>
            <Box sx={{ position: "relative", mb: 2 }}>
              {/* Capsule icon using SVG */}

              <Typography
                variant="h4"
                sx={{
                  background: `linear-gradient(135deg, #FFFFFF, ${classicTheme.accentColor})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontWeight: 700,
                  mb: 1,
                  letterSpacing: "-0.02em",
                  fontSize: { xs: "1.75rem", sm: "1.75rem" },
                }}
              >
                Create New Capsule
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  mb: 1,
                  lineHeight: 1.4,
                  fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  maxWidth: "90%",
                }}
              >
                Preserve your moments and create meaningful time capsules to unlock in the future.
              </Typography>
            </Box>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(10px)",
                p: isMobile ? 2.5 : 1,
                borderRadius: "16px",
                mb: { xs: 1.2, sm: 1.5 },
                border: "1px solid",
                borderColor: "rgba(255, 255, 255, 0.1)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 1,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: "1rem", sm: "1rem" },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 15,
                    delay: 0.4,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: classicTheme.accentColor,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <NotificationsActive sx={{ fontSize: 16, color: "#fff" }} />
                  </Box>
                </motion.div>
                Inspiration
              </Typography>

              <List sx={{ p: 0, mb: 0 }}>
                {[
                  "Create a vision board for your future self",
                  "Document a special trip or adventure",
                  "Capture memories from a special event",
                ].map((text, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0.2,
                      py: 0.5,
                      borderRadius: 2,
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "28px" }}>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          delay: 0.5 + index * 0.1,
                          type: "spring",
                          stiffness: 500,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: buttonGradient,
                            boxShadow: `0 0 10px ${classicTheme.accentColor}60`,
                          }}
                        />
                      </motion.div>
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: {
                          fontWeight: 500,
                          color: "rgba(255, 255, 255, 0.9)",
                          transition: "color 0.2s ease",
                          fontSize: { xs: "0.8rem", sm: "0.8rem" },
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </motion.div>

          <motion.div variants={itemVariants} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Button
              variant="contained"
              startIcon={
                <motion.div
                  animate={{ rotate: [0, 0, 90, 180, 270, 360] }}
                  transition={{
                    duration: 1.5,
                    times: [0, 0.2, 0.3, 0.4, 0, 1],
                    repeat: Number.POSITIVE_INFINITY,
                    repeatDelay: 5,
                  }}
                >
                  <Add />
                </motion.div>
              }
              onClick={handleOpenWizard}
              sx={{
                background: buttonGradient,
                color: "white",
                py: 0,
                px: { xs: 4, sm: 4 },
                borderRadius: 1,
                textTransform: "none",
                fontSize: { xs: "0.7rem", sm: "0.7rem" },
                fontWeight: 600,
                width: "100%",
                boxShadow: `0 8px 20px ${classicTheme.accentColor}40`,
                "&:hover": {
                  boxShadow: `0 12px 28px ${classicTheme.accentColor}60`,
                  background: buttonGradient,
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              Create
            </Button>
          </motion.div>
        </CardContent>
      </Card>
    )
  })

  const handleCloseRemoveAccessDialog = () => {
    setRemoveAccessDialogOpen(false)
  }

  const handleOpenDialog = (accessAmount: number) => {
    // First, fetch the friends list regardless of what happens next
    fetchFriendsList()

    // Then check subscription status for access limits
    if (user?.subscriptionStatus !== "premium") {
      if (accessAmount > 2) {
        // Non-premium users can only share with 2 people maximum
        setUpgradeModalOpen(true)
      } else {
        // Within limits, show dialog
        setOpenDialog(true)
      }
    } else {
      // Premium users can share with unlimited people
      setOpenDialog(true)
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedUsers([])
  }

  const handleDeleteCapsule = (capsuleId: number) => {
    setCapsuleToDelete(capsuleId)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteCapsule = async () => {
    if (capsuleToDelete) {
      setLoading(true)
      try {
        // Optimistically update the UI to remove the deleted capsule
        const updatedCapsules = capsules.filter((capsule) => capsule.capsuleID !== capsuleToDelete)

        // Check if there are still capsules left
        if (updatedCapsules.length > 0) {
          const newIndex = Math.min(currentCapsuleIndex, updatedCapsules.length - 1)
          setCurrentCapsuleIndex(newIndex) // Set to a valid index
          setSelectedTheme((updatedCapsules[newIndex].theme as ThemeType) || "classic")
        } else {
          // Handle the case where all capsules are deleted
          setCurrentCapsuleIndex(0)
          setSelectedTheme("classic")
        }

        setCapsules(updatedCapsules)
        localStorage.setItem("currentCapsuleIndex", currentCapsuleIndex.toString())

        // Ensure the deletion dialog is closed before continuing
        setDeleteDialogOpen(false)

        // Perform the actual deletion in the backend
        await DeleteCapsule(capsuleToDelete)

        console.log(`Capsule ${capsuleToDelete} deleted successfully.`)
      } catch (error) {
        console.error("Error deleting capsule:", error)
      } finally {
        setLoading(false)
        setCapsuleToDelete(null)
      }
    }
  }

  const renderCapsuleCards = useMemo(() => {
    const maxVisibleCards = Math.min(5, totalCards)
    const offsets = Array.from({ length: maxVisibleCards }, (_, i) => i - Math.floor(maxVisibleCards / 2))

    return offsets.map((offset) => {
      const index = (currentCapsuleIndex + offset + totalCards) % totalCards
      const isPlaceholder = index >= capsules.length

      const cardContent = isPlaceholder ? (
        <PlaceholderCapsule isActive={offset === 0} />
      ) : (
        <CapsuleCard
          capsule={capsules[index]}
          isActive={offset === 0}
          onDelete={() => handleDeleteCapsule(capsules[index].capsuleID)}
          onAddAccess={() => handleOpenDialog(capsules[index].access.length)}
          onRemoveAccess={() => setRemoveAccessDialogOpen(true)}
          isUnlocked={new Date() > new Date(capsules[index].unlock)}
          isOwner={capsules[index].owner === user?.username}
        />
      )

      return (
        <motion.div
          key={isPlaceholder ? `placeholder-${index}` : capsules[index].capsuleID}
          style={{
            position: "absolute",
            left: isMobile ? "15%" : "32%",
            top: "7%",
          }}
          initial={{
            x: `${offset * 100}%`,
            opacity: Math.abs(offset) < 1 ? 1 : 0.5,
            scale: 1 - Math.abs(offset) * 0.2,
            zIndex: maxVisibleCards - Math.abs(offset),
          }}
          animate={{
            x: `${offset * 100}%`,
            opacity: Math.abs(offset) < 1 ? 1 : 0.5,
            scale: 1 - Math.abs(offset) * 0.2,
            zIndex: maxVisibleCards - Math.abs(offset),
          }}
          exit={{
            x: `${offset * 110}%`,
            opacity: 0,
          }}
          transition={{
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        >
          {cardContent}
        </motion.div>
      )
    })
  }, [capsules, currentCapsuleIndex, isMobile, user, totalCards, theme])

  const fetchFriendsList = async () => {
    try {
      const { tokens } = await fetchAuthSession()
      const authToken = tokens?.accessToken?.toString()

      const response = await fetch(
        `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/friends/accepted/${user?.username}?status=accepted`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": authToken!,
          },
        },
      )
      if (!response.ok) {
        throw new Error("Failed to fetch friends list")
      }
      const result = await response.json()
      setFriends(result)
    } catch (error) {
      console.error("Error fetching friends list:", error)
    }
  }

  if (loading) {
    return (
      <ThemeProvider theme={baseTheme}>
        <Box
          sx={{
            minHeight: "100vh",
            width: "100%",
            pb: isMobile ? 3 : 0,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "background.default",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingCircle />
        </Box>
      </ThemeProvider>
    )
  }

  // Function to determine the time of day
  const getTimeOfDay = () => {
    const now = new Date()
    const hour = now.getHours()

    if (hour < 12) {
      return "Morning"
    } else if (hour < 18) {
      return "Afternoon"
    } else {
      return "Evening"
    }
  }

  return (
    <>
      <ThemeProvider theme={baseTheme}>
        <Box
          sx={{
            minHeight: "vh",
            pb: isMobile ? 3 : 0,
            position: isMobile && view !== "timeline" ? "fixed" : "relative",
            top: isMobile && view !== "timeline" ? 0 : "auto",
            left: isMobile && view !== "timeline" ? 0 : "auto",
            right: isMobile && view !== "timeline" ? 0 : "auto",
            bottom: isMobile && view !== "timeline" ? 0 : "auto",
            width: "100%",
            height: isMobile && view === "timeline" ? "auto" : "100%",
            bgcolor: isMobile ? "background.default" : "none",
            zIndex: 2,
            overflow: isMobile && view === "timeline" ? "visible" : isMobile ? "hidden" : "visible",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              pt: isMobile ? 1 : 10, // Increased padding for navbar space
              px: isMobile ? 1 : 4,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              mx: "auto", // Center content horizontally
              width: "100%",
            }}
          >
            <Box
              sx={{
                mb: isMobile ? 2 : 2,
                display: "flex",
                flexDirection: isMobile ? "column" : "column",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "flex-start",
                gap: isMobile ? 1 : 1,
                flexShrink: 0,
              }}
            >
              {/* Desktop Header */}
              {!isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    mb: 4,
                    mt: 4,
                  }}
                >
                  {/* Greeting Text */}
                  <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      color: "white",
                      letterSpacing: "-0.02em",
                      textAlign: "center",
                      mb: 3,
                    }}
                  >
                    Good {getTimeOfDay()}, {user?.name?.split(" ")[0] || "User"}
                  </Typography>

                  {/* Centered Toggle Button Group */}
                  <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, newView) => newView && setView(newView as "grid" | "timeline")}
                    aria-label="view mode"
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: 2,
                      p: 0.5,
                      border: "1px solid",
                      borderColor: "rgba(255, 255, 255, 0.1)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      mb: 4,
                      "& .MuiToggleButtonGroup-grouped": {
                        border: 0,
                        mx: 0.5,
                        borderRadius: 1.5,
                        fontSize: "0.875rem",
                        "&.Mui-selected": {
                          background: "linear-gradient(135deg, #7B2CBF80, #E31B8880)",
                          color: "white",
                          "&:hover": {
                            bgcolor: "rgba(123, 44, 191, 0.6)",
                          },
                        },
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.05)",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="grid" aria-label="grid view" sx={{ px: 2, py: 1, color: "text.secondary" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <GridView fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Grid
                        </Typography>
                      </Box>
                    </ToggleButton>
                    <ToggleButton
                      value="timeline"
                      aria-label="timeline view"
                      sx={{ px: 2, py: 1, color: "text.secondary" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Timeline fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Timeline
                        </Typography>
                      </Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Mobile Header - Inspired by modern mobile UIs */}
              {isMobile && (
                <>
                  {/* Main header container with minimal styling */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mt: 1
                    }}
                  >
                    {capsules.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0
                      }}
                    >
                      {/* User greeting with highlight */}
                      <Box>
                        <Typography
                          variant="h3"
                          component="h1"
                          sx={{
                            fontWeight: 600,
                            color: "white",
                            letterSpacing: "-0.02em",
                            textAlign: "center",
                            mb: 2,
                            mt: 1
                          }}
                        >
                          Good {getTimeOfDay()}, {user?.name?.split(" ")[0] || "User"}
                        </Typography>
                      </Box>

                    </Box>
                    )}

                    {/* View selector with caption */}
                    {capsules.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1
                        }}
                      >
                        {/* Modern segmented control style toggle */}
                        {/* Centered Toggle Button Group */}
                        <ToggleButtonGroup
                          value={view}
                          exclusive
                          onChange={(_, newView) => newView && setView(newView as "grid" | "timeline")}
                          aria-label="view mode"
                          sx={{
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            p: 0.5,
                            fontSize: '0.7rem',
                            border: "1px solid",
                            borderColor: "rgba(255, 255, 255, 0.1)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                            mb: 2,
                            "& .MuiToggleButtonGroup-grouped": {
                              border: 0,
                              mx: 0.5,
                              borderRadius: 1.5,
                              fontSize: "0.875rem",
                              "&.Mui-selected": {
                                background: "linear-gradient(135deg, #7B2CBF80, #E31B8880)",
                                color: "white",
                                "&:hover": {
                                  bgcolor: "rgba(123, 44, 191, 0.6)",
                                },
                              },
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.05)",
                              },
                            },
                          }}
                        >
                          <ToggleButton value="grid" aria-label="grid view" sx={{ px: 2, py: 1, color: "text.secondary" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <GridView fontSize="small" />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Grid
                              </Typography>
                            </Box>
                          </ToggleButton>
                          <ToggleButton
                            value="timeline"
                            aria-label="timeline view"
                            sx={{ px: 2, py: 1, color: "text.secondary" }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Timeline fontSize="small" />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Timeline
                              </Typography>
                            </Box>
                          </ToggleButton>
                        </ToggleButtonGroup>
                        {/* Carousel indicators - simplified dots */}
                        {view === "grid" && totalCards > 1 && (
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", mt: 1, mb: -5 }}>
                            {Array.from({ length: totalCards }).map((_, i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: i === currentCapsuleIndex
                                    ? "white"
                                    : "rgba(255,255,255,0.3)",
                                  transition: "all 0.3s ease"
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                position: "relative",
                height: "auto",
                overflowY: "visible",
              }}
            >
              {capsules.length > 0 ? (
                <motion.div
                  key={view}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ height: "100%" }}
                >
                  {view === "grid" && capsules.length > 0 ? (
                    isMobile ? (
                      // Mobile Carousel View
                      <Box
                        sx={{
                          position: "relative",
                          height: "100%",
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          overflow: "hidden",
                          mt: 0,
                        }}
                        {...handlers}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <AnimatePresence initial={false}>{renderCapsuleCards}</AnimatePresence>
                        </Box>
                      </Box>
                    ) : (
                      // Desktop Grid View
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)",
                            lg: "repeat(3, 1fr)",
                            xl: "repeat(3, 1fr)",
                          },
                          gap: { xs: 3, sm: 4, md: 5 },
                          px: { xs: 2, sm: 4, md: 5 },
                          py: { xs: 3, md: 5 },
                          width: "100%",
                          
                          margin: "0 auto",
                          alignItems: "stretch",
                          "& > *": {
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          },
                        }}
                      >
                        {/* Use windowing technique to only render visible items */}
                        {capsules.map((capsule, index) => (
                          <Box
                            key={capsule.capsuleID}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              width: "100%",
                              height: "100%",
                              // Use visibility property for a smoother scroll experience
                              // Elements still occupy space but aren't fully rendered when off-screen
                              visibility: "visible",
                            }}
                          >
                            {/* Only fully render capsule cards that are likely to be visible */}
                            <React.Suspense
                              fallback={
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    bgcolor: "background.paper",
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <CircularProgress size={24} />
                                </Box>
                              }
                            >
                              <CapsuleCard
                                capsule={{
                                  ...capsule,
                                  images: capsule.images.map((img) => ({
                                    ...img,
                                    image: img.image || "/polaroid.jpg",
                                  })),
                                }}
                                isGridView={true}
                                onDelete={() => handleDeleteCapsule(capsule.capsuleID)}
                                onAddAccess={() => {
                                  setCurrentCapsuleIndex(capsules.findIndex((c) => c.capsuleID === capsule.capsuleID))
                                  handleOpenDialog(capsule.access.length)
                                }}
                                onRemoveAccess={() => {
                                  setCurrentCapsuleIndex(capsules.findIndex((c) => c.capsuleID === capsule.capsuleID))
                                  setRemoveAccessDialogOpen(true)
                                }}
                                isUnlocked={new Date() > new Date(capsule.unlock)}
                                isOwner={capsule.owner === user?.username}
                              />
                            </React.Suspense>
                          </Box>
                        ))}
                        {capsules.length < 3 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              width: "100%",
                            }}
                          >
                            <PlaceholderCapsule isActive={false} />
                          </Box>
                        )}
                      </Box>
                    )
                  ) : (
                    // Timeline View - Using our new TimelineView component
                    <TimelineView capsules={memoizedImageUrls} />
                  )}
                </motion.div>
              ) : (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: 'background.default' }}>
                  <GetStartedScreen onCreateCapsule={handleOpenWizard} />
                </Box>
              )}
            </Box>
          </Container>

          {/* Enhanced Dialogs */}
          {joinCapsuleId && (
            <JoinCapsuleDialog open={!!joinCapsuleId} onClose={handleCloseJoinDialog} capsuleId={joinCapsuleId} />
          )}
          <AddAccessDialog
            open={openDialog}
            onClose={handleCloseDialog}
            currentCapsule={currentCapsule}
            user={user}
            friends={friends}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            handleAddAccess={handleAddAccess}
          />
          <DeleteCapsuleDialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={confirmDeleteCapsule}
          />
          <RemoveAccessDialog
            open={removeAccessDialogOpen}
            onClose={handleCloseRemoveAccessDialog}
            onConfirm={handleRemoveAccess}
          />

          {/* Lazy loaded modals */}
          <Suspense fallback={<LoadingScreen />}></Suspense>
        </Box>
      </ThemeProvider>
      <WizardModal open={isWizardOpen} onClose={handleCloseWizard} onSubmit={handleSubmitCapsule} />
      <UpgradeModal open={upgradeModalOpen} onClose={handleCloseUpgradeModal} />
    </>
  )
}
